import { InvocationStatus, InvocationRecord } from "@/shared/domain/actions";
import { useActiveInvocations } from "@/shared/domain/actions/useActiveInvocations";
import { Spinner, Tooltip } from "@/shared/foundation/primitives";

import styles from "./DatasetActionStatus.module.css";

interface DatasetActionStatusProps {
  datasetId: string;
}

/**
 * After the dataset's Actions tab label: a spinner while any invocation on
 * the dataset is pending or running, naming the counts on hover. Nothing when
 * none is.
 */
export function DatasetActionStatus({ datasetId }: DatasetActionStatusProps) {
  const invocationsQuery = useActiveInvocations(datasetId);

  const counts = invocationsQuery.data?.items.reduce(
    (
      acc: { pendingCount: number; runningCount: number },
      record: InvocationRecord,
    ) => {
      const status = record.status[record.status.length - 1].status;

      if (
        [InvocationStatus.Queued, InvocationStatus.Scheduled].includes(status)
      ) {
        acc.pendingCount++;
      } else if (
        [
          InvocationStatus.Downloading,
          InvocationStatus.Processing,
          InvocationStatus.Uploading,
        ].includes(status)
      ) {
        acc.runningCount++;
      }

      return acc;
    },
    { pendingCount: 0, runningCount: 0 },
  );

  if (!counts || (counts.pendingCount === 0 && counts.runningCount === 0)) {
    return null;
  }

  const { pendingCount, runningCount } = counts;
  const pendingString = pendingCount >= 9 ? "9+" : `${pendingCount}`;
  const runningString = runningCount >= 9 ? "9+" : `${runningCount}`;
  let activityString: string;
  if (pendingCount > 0 && runningCount > 0) {
    activityString = `${pendingString} Pending, ${runningString} Running`;
  } else if (pendingCount > 0) {
    activityString = `${pendingString} Pending`;
  } else {
    activityString = `${runningString} Running`;
  }

  return (
    <Tooltip>
      <Tooltip.Trigger render={<span className={styles.status} />}>
        <Spinner aria-label={activityString} />
      </Tooltip.Trigger>
      <Tooltip.Content>{activityString}</Tooltip.Content>
    </Tooltip>
  );
}
